import { useEffect } from 'react'
import { useDispatch } from 'react-redux'
import MapSocket from '../../services/socket/MapSocket'
import DataStoresActions from '../actions/data'
import RESTModelNormalizationSchemas from '../models/RESTModelNormalizationSchemas'
import StoreDevice from '../models/Device'
import StoreTrackingGroup from '../models/TrackingGroup'
import StoreMapRegion from '../models/MapRegion'
import ZoneDataStoreSlice from '../slices/data/ZoneDataStoreSlice'
import store from '../store'
import {
  mergeEntityMaps,
  buildMapPositionMergeEntities,
  buildLegacyCoordinateMergeEntities,
  getBufferedDeviceKeyForPositionPayload,
  getBufferedDeviceKeyForLegacyPayload
} from '../../modules/map/realtime/buildMapRealtimeMergeEntities'
import { buildTrackerIoMergeEntities } from '../../modules/trackers/trackerIoRealtime'
import { findDeviceIdForCoordinateUuid } from '../../modules/map/realtime/coordinatesCreatedMessage'

/**
 * Subscribes to real-time map data over socket.io and merges updates into Redux.
 * Call when the map screen is mounted; unsubscribes on unmount.
 *
 * High-frequency position events are coalesced per device per animation frame (rAF)
 * into a single mergeEntitiesIntoStores dispatch to limit renders and saga work.
 *
 * Backend must implement the map socket contract (see docs/socket/MAP_SOCKET_CONTRACT.md).
 */
const useMapRealtime = () => {
  const dispatch = useDispatch()

  useEffect(() => {
    MapSocket.joinMapContext({})

    let rafId = null
    /** @type {Map<string, { kind: 'position', payload: object } | { kind: 'legacy', raw: unknown }>} */
    const pendingByDevice = new Map()

    const flush = () => {
      rafId = null
      if (pendingByDevice.size === 0) return
      const entries = [...pendingByDevice.values()]
      pendingByDevice.clear()

      const devices = store.getState().data.devices
      const acc = {}
      for (const entry of entries) {
        const entities = entry.kind === 'position'
          ? buildMapPositionMergeEntities(devices, entry.payload)
          : buildLegacyCoordinateMergeEntities(devices, entry.raw)
        mergeEntityMaps(acc, entities)
      }
      if (Object.keys(acc).length > 0) {
        dispatch(DataStoresActions.mergeEntitiesIntoStores(acc))
      }
    }

    const scheduleFlush = () => {
      if (rafId != null) return
      rafId = window.requestAnimationFrame(flush)
    }

    const enqueuePosition = payload => {
      const devices = store.getState().data.devices
      const key = getBufferedDeviceKeyForPositionPayload(devices, payload)
      if (!key) return
      pendingByDevice.set(key, { kind: 'position', payload })
      scheduleFlush()
    }

    const enqueueLegacy = (...args) => {
      const raw = args.length >= 2 && typeof args[0] === 'string' && typeof args[1] === 'object'
        ? [args[0], args[1]]
        : args[0]
      const devices = store.getState().data.devices
      const key = getBufferedDeviceKeyForLegacyPayload(devices, raw)
      if (!key) return
      pendingByDevice.set(key, { kind: 'legacy', raw })
      scheduleFlush()
    }

    const mergeDevice = payload => {
      const device = payload?.device ?? payload
      if (!device?.id) return
      const dataState = store.getState().data
      const devices = dataState.devices
      const resolvedId = findDeviceIdForCoordinateUuid(devices, device.id) || String(device.id)
      const existing = devices[resolvedId]
      const deviceScalars = { ...device }
      delete deviceScalars.inputs
      delete deviceScalars.outputs
      const mergedPayload = existing
        ? {
            ...existing,
            ...deviceScalars,
            id: resolvedId,
            coordinateIds: Array.isArray(deviceScalars.coordinateIds) && deviceScalars.coordinateIds.length > 0
              ? deviceScalars.coordinateIds
              : existing.coordinateIds
          }
        : { ...deviceScalars, id: resolvedId }
      const storeDevice = StoreDevice.FromNormalizedRESTModel(mergedPayload)
      const entities = {
        [RESTModelNormalizationSchemas.device.entityName]: {
          [resolvedId]: storeDevice
        }
      }
      mergeEntityMaps(entities, buildTrackerIoMergeEntities(dataState, payload))
      dispatch(DataStoresActions.mergeEntitiesIntoStores(entities))
    }

    const mergeTrackingGroup = payload => {
      const trackingGroup = payload?.trackingGroup ?? payload
      if (!trackingGroup?.id) return
      const storeTg = StoreTrackingGroup.FromNormalizedRESTModel(trackingGroup)
      dispatch(
        DataStoresActions.mergeEntitiesIntoStores({
          [RESTModelNormalizationSchemas.trackingGroup.entityName]: {
            [storeTg.id]: storeTg
          }
        })
      )
    }

    const mergeMapRegion = payload => {
      const mapRegion = payload?.mapRegion ?? payload
      if (!mapRegion?.id) return
      const storeRegion = StoreMapRegion.FromNormalizedRESTModel(mapRegion)
      dispatch(
        DataStoresActions.mergeEntitiesIntoStores({
          [RESTModelNormalizationSchemas.mapRegion.entityName]: {
            [storeRegion.id]: storeRegion
          }
        })
      )
    }

    const refetchZone = payload => {
      const zoneId = payload?.zoneId
      if (!zoneId) return
      // Light socket payload: refetch the zone via REST so the API DTO stays authoritative.
      dispatch(ZoneDataStoreSlice.actions.fetch([String(zoneId)]))
    }

    const removeZone = payload => {
      const zoneId = payload?.zoneId
      if (!zoneId) return
      dispatch(ZoneDataStoreSlice.actions.deleteItem(String(zoneId)))
    }

    MapSocket.onDevicePositionUpdated(enqueuePosition)
    MapSocket.onCoordinatesCreated(enqueueLegacy)
    MapSocket.onDeviceUpdated(mergeDevice)
    MapSocket.onTrackingGroupUpdated(mergeTrackingGroup)
    MapSocket.onMapRegionUpdated(mergeMapRegion)
    MapSocket.onZoneUpdated(refetchZone)
    MapSocket.onZoneRemoved(removeZone)

    return () => {
      if (rafId != null) {
        window.cancelAnimationFrame(rafId)
        rafId = null
      }
      if (pendingByDevice.size > 0) {
        flush()
      }
      MapSocket.leaveMapContext({})
      MapSocket.removeAllMapListeners()
    }
  }, [dispatch])
}

export default useMapRealtime
